import { BaseApiService } from './BaseApiService';

export type BloomStockItemKind = 'ingredient' | 'box' | 'container';

export interface BulkProduct {
  id: number;
  name: string;
  sku_number: string;
  vendor?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateBulkProductData {
  name: string;
  sku_number: string;
  vendor?: string;
}

export interface UpdateBulkProductData {
  name?: string;
  sku_number?: string;
  vendor?: string;
}

export interface BloomStockItem extends BulkProduct {
  is_bloom_stock: boolean;
  item_kind: BloomStockItemKind;
  low_stock_threshold_cases: number;
  cases_on_hand: number;
  is_low: boolean;
}

export interface CreateBloomStockData {
  name: string;
  sku_number: string;
  vendor?: string;
  item_kind: BloomStockItemKind;
  low_stock_threshold_cases?: number;
  initial_cases?: number;
}

export interface UpdateBloomStockData {
  name?: string;
  sku_number?: string;
  vendor?: string;
  item_kind?: BloomStockItemKind;
  low_stock_threshold_cases?: number;
}

export interface BloomStockAdjustmentResult {
  item: BloomStockItem;
  cases_before: number;
  cases_after: number;
  cases_adjusted: number;
  became_low: boolean;
  is_low: boolean;
}

export interface BloomStockImportResult {
  applied: BloomStockAdjustmentResult[];
  errors: { row: number; sku_number?: string; message: string }[];
}

export interface Pagination {
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

const empty_pagination: Pagination = { total: 0, totalPages: 0, currentPage: 1, pageSize: 20 };

class BulkProductService extends BaseApiService {
  async getAllBulkProducts(): Promise<BulkProduct[]> {
    const response = await this.get<{ success: boolean; data: BulkProduct[] }>('/bulk-products');
    return response.data || [];
  }

  async getBulkProductsPaginated(
    page = 1,
    pageSize = 20,
    search?: string
  ): Promise<{
    bulkProducts: BulkProduct[];
    pagination: Pagination;
  }> {
    const params: Record<string, string> = {
      page: page.toString(),
      pageSize: pageSize.toString(),
    };
    if (search) params.search = search;
    const response = await this.get<{
      success: boolean;
      data: BulkProduct[];
      pagination: Pagination;
    }>('/bulk-products', params);

    return {
      bulkProducts: response.data || [],
      pagination: response.pagination || empty_pagination,
    };
  }

  async searchBulkProducts(searchTerm: string): Promise<BulkProduct[]> {
    const params = { search: searchTerm };
    const response = await this.get<{ success: boolean; data: BulkProduct[] }>(
      '/bulk-products',
      params
    );
    return response.data || [];
  }

  async getBulkProductById(id: number): Promise<BulkProduct> {
    const response = await this.get<{ success: boolean; data: BulkProduct }>(
      `/bulk-products/${id}`
    );
    if (!response.data) {
      throw new Error('Bulk product not found');
    }
    return response.data;
  }

  async createBulkProduct(data: CreateBulkProductData): Promise<BulkProduct> {
    const response = await this.post<{ success: boolean; data: BulkProduct }>(
      '/bulk-products',
      data
    );
    if (!response.data) {
      throw new Error('Failed to create bulk product');
    }
    return response.data;
  }

  async updateBulkProduct(id: number, data: UpdateBulkProductData): Promise<BulkProduct> {
    const response = await this.put<{ success: boolean; data: BulkProduct }>(
      `/bulk-products/${id}`,
      data
    );
    if (!response.data) {
      throw new Error('Failed to update bulk product');
    }
    return response.data;
  }

  async deleteBulkProduct(id: number): Promise<string> {
    const response = await this.delete<{ success: boolean; message: string }>(
      `/bulk-products/${id}`
    );
    return response.message || 'Bulk product deleted successfully';
  }

  async getBloomStockPaginated(
    page = 1,
    pageSize = 20,
    search?: string,
    filter?: 'all' | 'low'
  ): Promise<{
    items: BloomStockItem[];
    pagination: Pagination;
  }> {
    const params: Record<string, string> = {
      page: page.toString(),
      pageSize: pageSize.toString(),
    };
    if (search) params.search = search;
    if (filter && filter !== 'all') params.filter = filter;

    const response = await this.get<{
      success: boolean;
      data: BloomStockItem[];
      pagination: Pagination;
    }>('/bulk-products/stock', params);

    return {
      items: response.data || [],
      pagination: response.pagination || empty_pagination,
    };
  }

  async getBloomStockSummary(): Promise<{ total_count: number; low_count: number }> {
    const response = await this.get<{
      success: boolean;
      data: { total_count: number; low_count: number };
    }>('/bulk-products/stock/summary');
    return response.data || { total_count: 0, low_count: 0 };
  }

  async createBloomStock(data: CreateBloomStockData): Promise<BloomStockItem> {
    const response = await this.post<{ success: boolean; data: BloomStockItem }>(
      '/bulk-products/stock',
      data
    );
    if (!response.data) {
      throw new Error('Failed to create Bloom stock item');
    }
    return response.data;
  }

  async updateBloomStock(id: number, data: UpdateBloomStockData): Promise<BloomStockItem> {
    const response = await this.put<{ success: boolean; data: BloomStockItem }>(
      `/bulk-products/stock/${id}`,
      data
    );
    if (!response.data) {
      throw new Error('Failed to update Bloom stock item');
    }
    return response.data;
  }

  async adjustBloomStock(
    id: number,
    cases_adjusted: number,
    reason?: string
  ): Promise<BloomStockAdjustmentResult> {
    const response = await this.post<{ success: boolean; data: BloomStockAdjustmentResult }>(
      `/bulk-products/stock/${id}/adjust`,
      { cases_adjusted, reason }
    );
    if (!response.data) {
      throw new Error('Failed to adjust Bloom stock');
    }
    return response.data;
  }

  async importBloomStock(file_base64: string, file_name: string): Promise<BloomStockImportResult> {
    const response = await this.post<{ success: boolean; data: BloomStockImportResult }>(
      '/bulk-products/stock/import',
      { file_base64, file_name }
    );
    if (!response.data) {
      throw new Error('Failed to import Bloom stock');
    }
    return response.data;
  }
}

export const bulkProductService = new BulkProductService();
